import type { FilterBuilderSide } from '@talos/kyoko';
import { createContext, useCallback, useContext, useMemo, useRef, type PropsWithChildren } from 'react';
import type { SpaceParameterKey } from './types';

export type OpenSpaceParameterClause = (
  property: SpaceParameterKey,
  side?: FilterBuilderSide,
  value?: string | string[]
) => void;

interface SpaceParameterOpenClauseContextValue {
  registerOpenClause: (id: string, openClause: OpenSpaceParameterClause) => () => void;
  openClause: OpenSpaceParameterClause;
}

const SpaceParameterOpenClauseContext = createContext<SpaceParameterOpenClauseContextValue | undefined>(undefined);

export function SpaceParameterOpenClauseProvider({ children }: PropsWithChildren) {
  const openClauseRegistrationRef = useRef<{ id: string; openClause: OpenSpaceParameterClause }>();

  const registerOpenClause = useCallback((id: string, openClause: OpenSpaceParameterClause) => {
    const activeRegistration = openClauseRegistrationRef.current;
    if (activeRegistration != null) {
      throw new Error(
        `Space parameter open-clause handler already has controller "${activeRegistration.id}"; cannot register "${id}".`
      );
    }
    const registration = { id, openClause };
    openClauseRegistrationRef.current = registration;
    return () => {
      if (openClauseRegistrationRef.current === registration) {
        openClauseRegistrationRef.current = undefined;
      }
    };
  }, []);

  const openClause = useCallback<OpenSpaceParameterClause>((property, side, value) => {
    openClauseRegistrationRef.current?.openClause(property, side, value);
  }, []);

  const value = useMemo<SpaceParameterOpenClauseContextValue>(
    () => ({ registerOpenClause, openClause }),
    [openClause, registerOpenClause]
  );

  return <SpaceParameterOpenClauseContext.Provider value={value}>{children}</SpaceParameterOpenClauseContext.Provider>;
}

export function useOptionalSpaceParameterOpenClause() {
  return useContext(SpaceParameterOpenClauseContext);
}
